import * as classNames from 'classnames';
import { IActivity } from 'common/interfaces/activity';
import { IconButton } from 'components/action';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as moment from 'moment';
import * as React from 'react';
import { CSSTransitionGroup } from 'react-transition-group';
import { DropdownContainer, OptionsList } from '../list/index';

// Styles
const s = require('./ReminderContent.css');
const cancelIcon = require('assets/close orange icon.svg');

// Props
interface IReminderContentProps {
    activity: IActivity;
    zIndex: number;
    onSnoozeOptionsDropdownOpen: (activityId: number) => void;
    onSnoozeOptionSelected: (
        activityId: number,
        delayInMinutes: number
    ) => void;
    onGoToLeadClick: (leadId: number) => void;
    onDismissReminder: (activityId: number) => void;
    onMarkAsComplete: (activityId: number) => void;
}

// Component
@observer
export class ReminderContent extends React.Component<
    IReminderContentProps,
    {}
> {
    constructor(props: IReminderContentProps) {
        super(props);

        setTimeout(() => {
            this.show = true;
        }, 256);
    }

    @observable private show: boolean = false;
    @observable private showSnoozeOptions: boolean = false;
    @observable private showDismissConfirmation: boolean = false;

    @computed
    private get remindMeLaterOptions(): Array<{
        value: number;
        label: string;
    }> {
        return [
            {
                value: null,
                label: 'Cancel',
            },
            {
                value: 3,
                label: 'Remind me in 3 minutes',
            },
            {
                value: 5,
                label: 'Remind me in 5 minutes',
            },
            {
                value: 10,
                label: 'Remind me in 10 minutes',
            },
            {
                value: 15,
                label: 'Remind me in 15 minutes',
            },
            {
                value: 30,
                label: 'Remind me in 30 minutes',
            },
            {
                value: 45,
                label: 'Remind me in 45 minutes',
            },
            {
                value: 60,
                label: 'Remind me in 1 hour',
            },
            {
                value: 120,
                label: 'Remind me in 2 hours',
            },
            {
                value: 180,
                label: 'Remind me in 3 hours',
            },
            {
                value: 720,
                label: 'Remind me in 12 hours',
            },
            {
                value: 1440,
                label: 'Remind me in 24 hours',
            },
            {
                value: 10080,
                label: 'Remind me in 1 week',
            },
            {
                value: 20160,
                label: 'Remind me in 2 weeks',
            },
            {
                value: 43200,
                label: 'Remind me in 1 month',
            },
            {
                value: 86400,
                label: 'Remind me in 2 months',
            },
        ];
    }

    public render() {
        const { activity } = this.props;

        return (
            <div
                className={classNames(s.reminder, {
                    [s.show]: this.show,
                })}
                style={{
                    zIndex: this.props.zIndex,
                }}
            >
                <div className={s.content}>
                    <div className={s.activity}>
                        <p className={s.time}>
                            <strong>
                                {moment(activity.activityTime).format(
                                    'dddd, MMM, Do - hh:mm'
                                )}
                            </strong>
                            <span>
                                {moment(activity.activityTime).format('A')}
                            </span>
                        </p>

                        <h3 className={s.title}>
                            {activity.activityType !== null &&
                            typeof activity.activityType.name !== 'undefined'
                                ? activity.activityType.name
                                : activity.activityTypeId}
                        </h3>

                        {activity.note && (
                            <p className={s.note}>{activity.note}</p>
                        )}

                        <h5 className={s.lead}>
                            {activity.provider &&
                                typeof activity.provider.dba !== 'undefined' &&
                                activity.provider.dba && (
                                    <strong>{activity.provider.dba}</strong>
                                )}
                            {activity.provider &&
                                typeof activity.provider.legalBusName !==
                                    'undefined' &&
                                activity.provider.legalBusName && (
                                    <em>{activity.provider.legalBusName}</em>
                                )}
                            {activity.providerId !== null &&
                                this.props.onGoToLeadClick !== null && (
                                    <button onClick={this.handleNavigateToLead}>
                                        Go to lead
                                    </button>
                                )}
                        </h5>
                    </div>

                    <div className={s.actions}>
                        {this.showSnoozeOptions === false && (
                            <div className={s.quickActions}>
                                <button
                                    className="boxButton boxButtonWhite"
                                    onClick={this.handleToggleSnooze}
                                >
                                    Remind me later
                                </button>
                                <button
                                    className="boxButton boxButtonWhiteSuccess"
                                    onClick={this.handleMarkAsComplete}
                                >
                                    Mark as complete
                                </button>
                            </div>
                        )}
                        <div
                            className={classNames(s.snoozeActions, {
                                [s.show]: this.showSnoozeOptions,
                            })}
                        >
                            <DropdownContainer
                                ref={this.referenceReminderDropdown}
                                label="Snooze and remind me later in..."
                            >
                                <OptionsList
                                    value="none"
                                    label="Snooze reminder"
                                    onChangeOnlyValue={this.handleSnooze}
                                    options={this.remindMeLaterOptions}
                                />
                            </DropdownContainer>
                        </div>
                    </div>

                    <IconButton
                        className={s.dismiss}
                        onClick={this.handleDismissValidation}
                        icon={cancelIcon}
                        label="Dismiss"
                        labelOnRight={false}
                    />
                </div>

                <CSSTransitionGroup
                    component="div"
                    transitionName="dismissal-transition"
                    transitionAppear={true}
                    transitionEnter={true}
                    transitionLeave={true}
                    transitionAppearTimeout={500}
                    transitionEnterTimeout={500}
                    transitionLeaveTimeout={300}
                >
                    {this.showDismissConfirmation && (
                        <div className={s.dismissConfirmation}>
                            <p className={s.dismissLabel}>Are you sure?</p>

                            <div className={s.dismissContent}>
                                <p>
                                    Reminder for this activity will be disabled.
                                </p>

                                <button
                                    className={classNames(
                                        'boxButton',
                                        'boxButtonImportant'
                                    )}
                                    onClick={this.handleReminderDismissal}
                                >
                                    Yes, dismiss reminder
                                </button>

                                <button
                                    className={s.dismissCancelButton}
                                    onClick={this.handleDismissCancellation}
                                >
                                    No, keep the reminder
                                </button>
                            </div>
                        </div>
                    )}
                </CSSTransitionGroup>
            </div>
        );
    }

    private reminderDropdown: DropdownContainer = null;
    private referenceReminderDropdown = ref => (this.reminderDropdown = ref);

    @action
    private handleDismissValidation = () => {
        this.showDismissConfirmation = true;
    };

    @action
    private handleDismissCancellation = () => {
        this.showDismissConfirmation = false;
    };

    @action
    private handleReminderDismissal = () => {
        this.showDismissConfirmation = false;

        if (this.props.onDismissReminder) {
            this.props.onDismissReminder(this.props.activity.id);
        }
    };

    private handleNavigateToLead = () => {
        if (this.props.onGoToLeadClick) {
            this.props.onGoToLeadClick(this.props.activity.providerId);
        }
    };

    private handleMarkAsComplete = () => {
        this.props.onMarkAsComplete(this.props.activity.id);
    };

    private handleToggleSnooze = () => {
        const next = !this.showSnoozeOptions;

        this.showSnoozeOptions = next;

        setTimeout(() => {
            if (this.reminderDropdown) {
                if (next === true) {
                    this.reminderDropdown.openDropdown();
                    this.props.onSnoozeOptionsDropdownOpen(
                        this.props.activity.id
                    );
                } else {
                    this.reminderDropdown.closeDropdown();
                }
            }
        }, 128);
    };

    private handleSnooze = (remindInMinutes: number) => {
        if (remindInMinutes === null) {
            this.handleToggleSnooze();
            return;
        }

        this.props.onSnoozeOptionSelected(
            this.props.activity.id,
            remindInMinutes
        );
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/reminder/ReminderContent.tsx